import { useEffect, useRef, useState, useCallback } from 'react'

export function useAudioCapture(enabled: boolean, onAudioFrame: (pcm: Int16Array) => void) {
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ctxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const processorRef = useRef<ScriptProcessorNode | null>(null)

  useEffect(() => {
    if (!enabled) {
      if (processorRef.current) processorRef.current.disconnect()
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
      setIsReady(false)
      return
    }

    let cancelled = false

    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return }

        const ctx = new AudioContext({ sampleRate: 16000 })
        ctxRef.current = ctx
        streamRef.current = stream

        const source = ctx.createMediaStreamSource(stream)

        // Use ScriptProcessorNode for broad compatibility (AudioWorklet may fail in headless)
        const processor = ctx.createScriptProcessor(256, 1, 1) // 256 samples = 16ms at 16kHz
        processorRef.current = processor

        processor.onaudioprocess = (e) => {
          const samples = e.inputBuffer.getChannelData(0)
          const pcm = new Int16Array(samples.length)
          for (let i = 0; i < samples.length; i++) {
            pcm[i] = Math.max(-32768, Math.min(32767, samples[i] * 32767))
          }
          onAudioFrame(pcm)
        }

        source.connect(processor)
        processor.connect(ctx.destination) // required for ScriptProcessorNode to fire

        setIsReady(true)
        console.log('[Audio] capture started, 16kHz, 20ms frames')
      } catch (err) {
        console.error('[Audio] capture init failed:', err)
        setError(String(err))
      }
    }

    init()

    return () => {
      cancelled = true
      if (processorRef.current) processorRef.current.disconnect()
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null }
      if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null }
    }
  }, [enabled, onAudioFrame])

  return { isReady, error }
}
